import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, PhoneCall, MapPin, AlertTriangle, Briefcase, Stethoscope, Users, Heart, ClipboardList, Clock, TrendingDown, HeartHandshake, Award } from 'lucide-react';
const capabilities = [{
  icon: PhoneCall,
  name: 'Telephonic Case Management',
  description: 'Experienced RN case managers coordinate care, communicate with providers, and remove obstacles to recovery — all via phone.'
}, {
  icon: MapPin,
  name: 'Field & On-Site CM',
  description: 'In-person nurse presence at provider appointments and facility visits when claims require it.'
}, {
  icon: AlertTriangle,
  name: 'Catastrophic CM',
  description: 'Specialized clinical case managers for traumatic brain injury, spinal cord, burn, and other catastrophic claims.'
}, {
  icon: Briefcase,
  name: 'Return-to-Work Coordination',
  description: 'Active collaboration with employers, treating physicians, and injured workers to facilitate modified and full-duty return.'
}, {
  icon: Stethoscope,
  name: 'Treatment Plan Oversight',
  description: 'Ongoing review of medical care to ensure alignment with evidence-based guidelines and recovery goals.'
}, {
  icon: ClipboardList,
  name: 'Vocational Rehabilitation',
  description: "When return to the same job isn't possible, our team coordinates retraining and job placement support."
}, {
  icon: Users,
  name: 'Family & Caregiver Support',
  description: 'Education and resources for family members supporting the injured worker through recovery.'
}, {
  icon: Heart,
  name: 'Behavioral Health Coordination',
  description: 'Recognizing the mental health dimensions of injury — and connecting workers to appropriate support.'
}];
const benefits = [{
  icon: Clock,
  title: 'Shorter claim duration',
  stat: '34%',
  description: 'Average reduction in claim duration on cases with early case management intervention.'
}, {
  icon: TrendingDown,
  title: 'Lower total claim cost',
  stat: '$3:1',
  description: 'For every dollar invested in case management, our clients see approximately $3 in claim cost savings.'
}, {
  icon: HeartHandshake,
  title: 'Higher satisfaction',
  stat: '95%',
  description: 'Of injured workers report feeling supported and understood throughout their recovery.'
}, {
  icon: Award,
  title: 'Credentialed nurses',
  stat: 'CCM',
  description: 'Case managers hold CCM, COHN, CDMS, or CRRN certifications — and average 15+ years of experience.'
}];
export function CaseManagement() {
  return <div className="bg-brand-paper" data-id="element-512">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-513">
        <div className="absolute inset-0 opacity-10" data-id="element-514">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-515"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-516"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-517">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-518">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-519">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-520">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-521">
              Case <span className="text-brand-gold italic" data-id="element-522">Management</span>
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-523">
              Experienced RN case managers guide injured workers from injury to
              recovery — coordinating care, removing barriers, and accelerating
              return to work.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-524">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-525">
                Request a Consultation <ArrowRight className="w-5 h-5 ml-2" data-id="element-526" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-527">
                <Phone className="w-5 h-5 mr-2" data-id="element-528" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-529">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-530">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-531">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-532"></div>
            <img src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Nurse case manager guiding a patient through their recovery plan" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-533" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-534">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-535">
              Why Case Management
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-536">
              Every claim deserves a clinical advocate.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-537">
              When an injured worker has a knowledgeable nurse in their corner,
              recovery accelerates. Treatment plans stay on track. Communication
              breakdowns disappear. Costs come down — not because we cut care,
              but because we coordinate it.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-538">
              Our case managers are experienced RNs, most with 15+ years in
              workers' compensation. They don't just manage paperwork — they
              build trust with injured workers and influence outcomes through
              clinical expertise.
            </p>
            <ul className="space-y-4" data-id="element-539">
              {['Dedicated nurse case manager per claim', 'Credentialed RNs (CCM, COHN, CRRN, CDMS)', 'Telephonic, field, and catastrophic capabilities', 'Return-to-work focus from day one'].map(item => <li key={item} className="flex items-start" data-id="element-540">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-541" />
                  <span className="text-gray-800 font-medium" data-id="element-542">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-543">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-544">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-545">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-546">
              Capabilities
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-547">
              A complete case management program.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-548">
              From a single phone call to catastrophic on-site advocacy — we
              match the right level of intervention to each claim.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-549">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-550">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-551">
                    <Icon className="w-6 h-6" data-id="element-552" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-553">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-554">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-555">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-556">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-557">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-558">
              Proven Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-559">
              Better recovery. Lower cost. Every time.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-560">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-561">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-562"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-563" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-564">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-565">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-566">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-567">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-568"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-569">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-570">
            Bring a clinical advocate to every claim.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-571">
            Talk to our team about how Arissa case management can transform
            outcomes for your injured workers and your bottom line.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-572">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-573">
              Schedule a Walkthrough
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-574">
              <Phone className="w-5 h-5 mr-2" data-id="element-575" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}